import { Timestamp } from "./timestamp";

/**
 * User profile document stored at `BusinessMember/{uid}`.
 * Contains identity fields only — no business-scoped access or roles.
 */
export interface BusinessMember {
  uid: string;
  name: string;
  email: string;
  isSuperAdmin?: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** @deprecated Use BusinessMembership.role instead. */
  role?: "admin" | "member";
  /** @deprecated No longer written; kept for legacy doc compat. */
  invitedAt?: Timestamp;
  language?: "sv" | "en";
  /** Mobile number given in the onboarding account step (GRA-171). */
  phone?: string;
}

export type MembershipRole = "owner" | "admin" | "member";

/**
 * The store role model from the platform sketch (2026-10): what a person sees
 * of the store. Presentation and delegation only; `role`/`accessLevel` keep
 * deciding what the server allows.
 *   super     = VD / Handlare (sees everything, sets goals and roles)
 *   adminchef = Butikschef (whole store: teams, profiles, 1:1, data, actions)
 *   admin     = Ledningsgrupp (own departments; no login data)
 *   medarb    = Medarbetare (own things + the store's shared KPIs)
 */
export type StoreRole = "super" | "adminchef" | "admin" | "medarb";
export const STORE_ROLES = ["super", "adminchef", "admin", "medarb"] as const;
export type MembershipStatus = "active" | "invited" | "removed";
/**
 * "full" = all product modules (Recruit, Leads, Teams, Intelligence).
 * "team" = team-scoped modules only (Teams, Intelligence, Pulse, Tests).
 * "none" = no people data at all (no Coach, Pulse, people, tests, insights).
 *   Used for report uploaders (GRA-96) who only handle store reports.
 * Absent/undefined is treated as "full" for backward compatibility.
 */
export type MembershipAccessLevel = "full" | "team" | "none";

export const MEMBERSHIP_ACCESS_LEVELS = ["full", "team", "none"] as const;

/** Who may see store report numbers without being a people-data viewer. */
export type ReportViewPermission = "none" | "store";

/**
 * Capabilities granted on top of role and access level. They combine with
 * any role, so a department manager can also be the report uploader.
 */
export interface MembershipPermissions {
  /** May import store reports (Artikelanalys etc.) for the whole business. */
  reportUpload?: boolean;
  /** "store" = may read the store's report numbers (no people data). */
  reportView?: ReportViewPermission;
}

/**
 * Membership document stored at `Businesses/{businessId}/members/{uid}`.
 * Single source of truth for "does this user have access to this business?"
 */
export interface BusinessMembership {
  uid: string;
  name: string;
  email: string;
  role: MembershipRole;
  status: MembershipStatus;
  joinedAt: Timestamp;
  invitedBy?: string;
  /** Absent = "full". Owners and admins always have full access regardless of this field. */
  accessLevel?: MembershipAccessLevel;
  /** Report capabilities (GRA-96). Owners and admins implicitly have all of them. */
  permissions?: MembershipPermissions;
  /** Sketch store role; absent = derived from `role`/`accessLevel`. */
  storeRole?: StoreRole;
}

/** The store role to show when none is stored: owners/admins are super, team-scoped are admin. */
export function effectiveStoreRole(membership: {
  role?: string | null;
  accessLevel?: string | null;
  storeRole?: string | null;
}): StoreRole {
  if (membership.storeRole && (STORE_ROLES as readonly string[]).includes(membership.storeRole)) {
    return membership.storeRole as StoreRole;
  }
  if (membership.role === "owner") return "super";
  if (membership.role === "admin") return "adminchef";
  if (membership.accessLevel === "team") return "admin";
  return "medarb";
}

type MembershipAccessInput = {
  role?: string | null;
  accessLevel?: string | null;
  permissions?: MembershipPermissions | null;
};

function isOwnerOrAdmin(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * Effective access level. Owners and admins are always "full"; unknown or
 * absent values read as "full" for backward compatibility.
 */
export function effectiveAccessLevel(
  membership: MembershipAccessInput,
): MembershipAccessLevel {
  if (isOwnerOrAdmin(membership.role)) return "full";
  if (membership.accessLevel === "team") return "team";
  if (membership.accessLevel === "none") return "none";
  return "full";
}

/** False for report-only accounts: they must never see Pulse, people or coach data. */
export function hasPeopleDataAccess(membership: MembershipAccessInput): boolean {
  return effectiveAccessLevel(membership) !== "none";
}

export function canUploadReports(membership: MembershipAccessInput): boolean {
  return (
    isOwnerOrAdmin(membership.role) ||
    membership.permissions?.reportUpload === true
  );
}

/**
 * May read the store's report numbers. People-data viewers see them through
 * the KPI views; report-only accounts need `reportView: "store"`.
 */
export function canViewStoreReports(membership: MembershipAccessInput): boolean {
  return (
    hasPeopleDataAccess(membership) ||
    membership.permissions?.reportView === "store"
  );
}

/**
 * @deprecated Use {@link BusinessMembership} instead.
 */
export type BusinessMemberReference = BusinessMembership;
